import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { registerChatbotOpenHandler, unregisterChatbotOpenHandler } from '@/hooks/useChatbotTrigger';
import { auctionItems } from '@/mocks/auctionItems';

const SYSTEM_INSTRUCTION = `
너는 대한민국 최고의 부동산 경매 권리분석 및 배당금 산출 전문 AI 엔진이자, 실전 경매·부동산 투자 교육 플랫폼 '강스터디'의 핵심 AI 비서다.

사용자가 경매 데이터를 입력하면 법원실무제요, 민사집행법, 주택임대차보호법에 준수하여 권리 소멸/인수 여부, 8단계 배당 시뮬레이션, 최종 인수금액, 위험 경고(Alert), 명도 난이도를 완벽하게 연산하여 결과를 출력한다. 또한 경매 용어나 물건에 대한 일반 문의에도 친절하고 전문적으로 답변한다.

---

## [권리분석 엔진 — STEP 1~7]

### STEP 1: 입력 데이터 파싱
사용자가 다음 항목을 제공하면 권리분석을 시작한다.
1. Auction_Info: 사건번호, 감정가, 최저가, 입찰예정가(V_bid), 경매개시결정등기일, 배당요구종기일
2. Registry_Rights: 등기부 권리 목록 (갑구/을구, 권리종류, 접수일자, 접수번호, 채권액, 건물전체여부, 배당요구여부)
3. Tenant_Info: 임차인 목록 (보증금, 월세, 전입일, 확정일자, 배당요구일, 재전입 여부)
4. Non_Registry_Info: 유치권 신고 여부, 당해세 금액, 일반세금 금액 및 법정기일, 위반건축물 여부

### STEP 2: 말소기준권리(EBR) 판별 및 등기부 분석
1. 후보군 추출: [근저당권, 저당권, 가압류, 압류, 담보가등기, 경매개시결정등기, 건물전체+배당요구(또는 경매신청)한 전세권]
2. 기준점 설정: 후보군 중 접수일자(및 접수번호)가 가장 빠른 권리를 '말소기준권리(EBR)'로 산출하고 그 접수일자를 EBR_Date로 지정
3. 소멸/인수 판별:
   - EBR 포함, EBR보다 접수일이 늦은 권리 → [소멸]
   - EBR보다 접수일이 빠른 권리 → [인수]
   - 예외: '건물철거 및 토지인도청구 가처분' → [무조건 인수 DANGER]
   - 예외: 선순위 '소유권이전청구권가등기' → [무조건 인수 CRITICAL DANGER]
   - 예외: 선순위 '전세권' 배당요구 없는 경우 → [무조건 인수]

### STEP 3: 임차인 대항력 및 인수액 판별
1. 대항력 효력발생일:
   - 재전입 이력 있는 경우: 재전입일 + 1일 00:00:00
   - 일반: 전입일 + 1일 00:00:00
2. 선/후순위 판별:
   - 대항력기준일 < EBR_Date → 선순위 임차인 (대항력 있음)
   - 대항력기준일 >= EBR_Date → 후순위 임차인 (대항력 소멸)
3. 선순위 임차인 인수 보증금 계산:
   - 배당요구 안 했거나 배당요구종기일 이후 → 보증금 전액 인수
   - 배당요구 한 경우 → 우선변제권 효력일(max(대항력일, 확정일자))로 STEP 4 배당 엔진 이관, 최종 인수금액 = max(0, 보증금 - 배당금)

### STEP 4: 8단계 정교화 배당 시뮬레이션
입찰예정가(V_bid)에서 집행비용을 뺀 금액을 배당재단(Pool)으로 설정하고 순서대로 충당:
- 0순위: 경매 집행비용 공제 (통상 낙찰가의 1~2%)
- 1순위: 필요비 / 유익비
- 2순위 (동순위 안분배당): 소액임차인 최우선변제금 (담보물권 설정일 기준 최우선변제 조견표 적용, 합계 Pool 1/2 한도) + 근로자 최우선 임금채권 (최종 3개월분 임금/3년치 퇴직금)
- 3순위: 당해세 (재산세, 종합부동산세)
  * 주택임대차보호법 개정 특례: 선순위 임차인 확정일자보다 늦은 당해세는 선순위 임차인 보증금을 먼저 배당 처리
- 4순위: 우선변제권 채권 (설정일/접수일/확정일자/세금 법정기일 시간순 순차배당)
  * 근저당, 확정일자 임차인, 당해세 외 세금, 공과금 간 일자 정밀 비교
  * 보증금 증액 계약: 최초 보증금과 증액분 분리하여 각 확정일자 순위로 배당
- 5순위: 일반 임금채권
- 6순위: 공과금 (국민건강보험, 국민연금 등)
- 7순위: 일반채권 (가압류, 강제경매 신청채권) → 남은 Pool 채권액 비율 안분배당

### STEP 5: 특수 위험 감지 및 Alert
- 대위변제 위험: 선순위 근저당 채권액이 감정가 5% 이하이고 후순위 임차인 있는 경우 Alert
- 유치권: 신고 있는 경우 등기부 미표시 권리 및 점유/견련성 검증 Alert
- 위반건축물: 건축물대장 위반건축물인 경우 이행강제금 및 대출 제한 Alert

### STEP 6: 명도 난이도 판별
- 인도명령 대상: 대항력 없는 후순위 임차인, 전 소유자, 채무자 → '인도명령 (1~2개월 소요, 난이도 낮음)'
- 명도소송 대상: 미배당 선순위 임차인, 유치권자 → '명도소송/협상 필요 (6개월~1년 소요, 난이도 높음)'

### STEP 7: 최종 Output 포맷
권리분석 결과는 아래 두 파트로 출력:

파트 1 — JSON (시스템 연동용):
{
  "case_number": "사건번호",
  "eradication_base_right": {"type": "권리명", "date": "YYYY-MM-DD", "status": "소멸"},
  "total_takeover_amount": 0,
  "expected_total_cost": 0,
  "eviction_info": {"type": "인도명령 또는 명도소송", "difficulty": "상/중/하"},
  "alerts": ["경고문구1", "경고문구2"]
}

파트 2 — 한국어 종합 리포트:
- ✅ 말소기준권리 판별: 어떤 권리가 EBR인지, 날짜와 함께 명확히
- ✅ 등기부 권리 소멸/인수 목록: 표 형태로 정리
- ✅ 임차인 대항력 및 배당 결과: 선/후순위, 인수금액 명시
- ✅ 배당 시뮬레이션 전 과정: 단계별 숫자 포함
- ✅ 예상 총 비용 (낙찰가 + 인수금액 + 기타 비용)
- ✅ 위험 Alert 요약
- ✅ 명도 난이도 및 전략
- ✅ 강스터디 실전반 수강 추천 멘트 (분석 결과와 자연스럽게 연결)

---

## [강스터디 플랫폼 데이터 — 일반 문의용]

### 등록된 추천 경매 물건
1. 사건번호: 2025타경52341 / 인천 미추홀구 용현동 / 아파트 / 최저가 224,000,000원 / 임차인 없음, 예상수익 높음
2. 사건번호: 2025타경90512 / 인천 부평구 부평동 / 아파트 / 최저가 147,000,000원 / 3회 유찰로 매우 저렴함
3. 사건번호: 2025타경68902 / 서울 관악구 봉천동 / 다세대(빌라) / 최저가 175,000,000원 / 임차인 있음 (권리분석 필수)

### 예산 상담 가이드
사용자가 예산을 말하면서 물건 추천 요청 시, 실제 등록 물건을 예시로 들어 설명하라. 낙찰가의 70~80% 경락잔금대출을 활용한 레버리지 전략을 안내하고, 상세 권리분석과 대출 전략은 강스터디 교육과정에서 배울 수 있다고 자연스럽게 연결하라.

---

## [응답 원칙]
- 권리분석 데이터가 입력된 경우: 반드시 STEP 1~7 전 과정을 실행하고 JSON + 한국어 리포트를 출력
- 일반 경매 문의: 친절하고 전문적인 경매 박사처럼 초보자도 이해할 수 있게 설명
- 항상 신뢰감 있는 전문가 톤 유지
- 임차인 있는 물건 언급 시 권리분석 중요성과 리스크 명확히 고지
- 모든 금액은 원화(원) 단위로, 숫자는 천 단위 콤마 표기
`;

const SIDEBAR_WIDTH = 380;
const CASE_REGEX = /(\d{4}\s*타경\s*\d+)/g;

// Gemini 키는 브라우저로 내려보내지 않는다 — VITE_ 변수는 번들에 문자 그대로
// 박히므로 공개 URL 에 올리면 누구나 빼서 쓴다. 대신 Pages Function 프록시
// (functions/api/chat.ts)가 서버 시크릿으로 호출하고 IP 단위 호출 제한을 건다.
class ChatbotUnavailableError extends Error {}

const CHATBOT_UNAVAILABLE_MESSAGE =
  'AI 상담은 지금 준비 중이에요. 상담 신청 페이지에서 전문가에게 바로 문의하실 수 있어요.';

/** 호출 제한에 걸렸을 때는 "고장" 이 아니라 "잠시 후" 로 안내해야 한다. */
class ChatbotRateLimitError extends Error {}

async function askGemini(contents: string): Promise<{ text: string }> {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message: contents, systemInstruction: SYSTEM_INSTRUCTION }),
  });

  if (res.status === 429) {
    const body = await res.json().catch(() => ({}));
    throw new ChatbotRateLimitError(body?.error || '질문이 너무 많아요. 잠시 후 다시 시도해 주세요.');
  }
  // 503 = 키 미설정(프록시가 fail-closed). 사용자에게는 "준비 중" 으로 보인다.
  if (res.status === 503) throw new ChatbotUnavailableError();
  if (!res.ok) throw new Error(`chat proxy ${res.status}`);

  const body = (await res.json()) as { text?: string };
  return { text: body.text ?? '' };
}

const QUICK_PROMPTS = [
  {
    icon: 'ri-search-eye-line',
    label: '오늘의 추천 소액 경매 물건 알려줘',
    message: '오늘의 추천 소액 경매 물건 알려줘',
  },
  {
    icon: 'ri-file-search-line',
    label: '사건번호 입력하고 권리분석 위험도 체크',
    message: '사건번호를 입력하고 권리분석 위험도를 체크하고 싶어. 예를 들어 사건번호 2025타경52341을 분석해줘.',
  },
  {
    icon: 'ri-book-open-line',
    label: '내게 맞는 경매 교육과정 추천받기',
    message: '내 상황에 맞는 경매 교육과정을 추천해줘. 나는 경매 초보자이고 예산은 1억원 정도야.',
  },
  {
    icon: 'ri-phone-line',
    label: '전문가 1:1 상담 연결하기',
    message: '전문가 1:1 상담을 받고 싶어. 어떤 상담 옵션이 있는지 알려줘.',
  },
  {
    icon: 'ri-sticky-note-line',
    label: '상담 요약 등록하기',
    message: '상담 요약: (사건번호, 핵심 내용을 입력해주세요)',
  },
];

interface CaseCardData {
  caseNumber: string;
  court: string;
  location: string;
  propertyType: string;
  appraisedValue: number;
  minimumPrice: number;
  failureCount: number;
  rightsAnalysis: string;
  grade: string;
  id: string;
}

interface Message {
  role: 'user' | 'model';
  content: string;
  timestamp: Date;
  caseCard?: CaseCardData;
  ctas?: { type: 'property' | 'learning'; userMessage: string };
}

function formatKRW(amount: number): string {
  if (amount >= 100000000) {
    const eok = Math.floor(amount / 100000000);
    const man = Math.floor((amount % 100000000) / 10000);
    return man > 0 ? `${eok}억 ${man.toLocaleString()}만원` : `${eok}억원`;
  }
  return `${amount.toLocaleString()}원`;
}

function detectCaseCard(text: string): CaseCardData | null {
  const cleaned = text.replace(/\s+/g, '');
  let foundCaseNumber = '';
  for (const item of auctionItems) {
    const cn = item.caseNumber.replace(/\s+/g, '');
    if (cleaned.includes(cn)) {
      foundCaseNumber = item.caseNumber;
      break;
    }
  }
  if (!foundCaseNumber) {
    const match = cleaned.match(/(\d{4}타경\d+)/);
    if (match) foundCaseNumber = match[1];
  }
  if (!foundCaseNumber) return null;
  const item = auctionItems.find(
    (a) => a.caseNumber.replace(/\s+/g, '') === foundCaseNumber.replace(/\s+/g, ''),
  );
  if (!item) return null;
  return {
    caseNumber: item.caseNumber,
    court: item.court,
    location: item.location,
    propertyType: item.propertyType,
    appraisedValue: item.appraisedValue,
    minimumPrice: item.minimumPrice,
    failureCount: item.failureCount,
    rightsAnalysis: item.rightsAnalysis,
    grade: item.grade,
    id: item.id,
  };
}

function detectCTAContext(userMessage: string): 'property' | 'learning' {
  const propertyKws = [
    '물건', '권리분석', '사건번호', '경매물건', '배당', '임차인',
    '근저당', '저당권', '말소', '등기부', '감정가', '최저가', '입찰',
    '낙찰', '명도', '유찰', '법원', '경매', '투자', '수익',
    '임대차', '전세', '보증금',
  ];
  const learningKws = [
    '공부', '강의', '교육', '용어', '배우', '학습', '수강',
    '과정', '초보', '입문', '기초', '추천', '교육과정',
    '강좌', '커리큘럼',
  ];

  const msg = userMessage.toLowerCase();
  const propScore = propertyKws.filter((kw) => msg.includes(kw)).length;
  const learnScore = learningKws.filter((kw) => msg.includes(kw)).length;

  if (propScore >= learnScore) return 'property';
  return 'learning';
}

export default function Chatbot() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'model',
      content:
        '안녕하세요! 강스터디 AI 경매비서입니다. 무엇을 도와드릴까요?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isTyping, setIsTyping] = useState(false);

  const showQuickPrompts = useMemo(
    () => isOpen && messages.length === 1,
    [isOpen, messages.length],
  );

  const sendMessageDirect = useCallback(async (content: string) => {
    if (!content.trim()) return;
    const caseCard = detectCaseCard(content);
    const userMessage: Message = {
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
      caseCard: caseCard || undefined,
      ctas: { type: detectCTAContext(content), userMessage: content.trim() },
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await askGemini(content.trim());
      const text =
        response.text ||
        '죄송해요, 답변을 생성하지 못했어요. 다시 시도해 주세요.';
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          content: text,
          timestamp: new Date(),
          ctas: { type: detectCTAContext(content), userMessage: content.trim() },
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          content:
            err instanceof ChatbotUnavailableError
              ? CHATBOT_UNAVAILABLE_MESSAGE
              : err instanceof ChatbotRateLimitError
                ? err.message
                : '죄송해요, 일시적인 오류가 발생했어요. 잠시 후 다시 시도해 주세요.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  const openChatbot = useCallback(
    (prefillMessage?: string, autoSend?: boolean) => {
      setIsOpen(true);
      if (prefillMessage) {
        if (autoSend) {
          setTimeout(() => sendMessageDirect(prefillMessage), 450);
        } else {
          setInput(prefillMessage);
        }
      }
    },
    [sendMessageDirect],
  );

  useEffect(() => {
    registerChatbotOpenHandler(openChatbot);
    return () => unregisterChatbotOpenHandler();
  }, [openChatbot]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const originalPadding = document.body.style.paddingRight;
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${SIDEBAR_WIDTH + scrollbarWidth}px`;
      return () => {
        document.body.style.paddingRight = originalPadding;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;
    const caseCard = detectCaseCard(input);
    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
      caseCard: caseCard || undefined,
      ctas: { type: detectCTAContext(input), userMessage: input.trim() },
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(false);
    setLoading(true);

    try {
      const response = await askGemini(userMessage.content);
      const text =
        response.text ||
        '죄송해요, 답변을 생성하지 못했어요. 잠시 후 다시 시도해 주세요.';
      const modelMessage: Message = {
        role: 'model',
        content: text,
        timestamp: new Date(),
        ctas: {
          type: detectCTAContext(userMessage.content),
          userMessage: userMessage.content,
        },
      };
      setMessages((prev) => [...prev, modelMessage]);
    } catch (err) {
      const errorMessage: Message = {
        role: 'model',
        content:
          err instanceof ChatbotUnavailableError
            ? CHATBOT_UNAVAILABLE_MESSAGE
            : err instanceof ChatbotRateLimitError
              ? err.message
              : '죄송해요, 일시적인 오류가 발생했어요. 잠시 후 다시 시도해 주세요.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  const handleQuickPrompt = (message: string) => {
    sendMessageDirect(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const userMessageCount = messages.filter((m) => m.role === 'user').length;

  return (
    <>
      {/* Collapsed Tab Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 50, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            onClick={() => setIsOpen(true)}
            className="fixed top-1/2 right-0 z-[9999] -translate-y-1/2 cursor-pointer"
            aria-label="AI 챗봇 열기"
          >
            <div className="w-11 h-52 bg-primary-500 text-white rounded-l-xl shadow-2xl flex flex-col items-center justify-center gap-3 hover:bg-primary-600 transition-colors">
              <i className="ri-robot-2-line text-xl"></i>
              <span
                className="text-xs font-bold tracking-widest"
                style={{ writingMode: 'vertical-rl' }}
              >
                AI 경매 비서
              </span>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[9998] bg-black/30 cursor-pointer"
            />
            <motion.div
              initial={{ x: SIDEBAR_WIDTH }}
              animate={{ x: 0 }}
              exit={{ x: SIDEBAR_WIDTH }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 z-[9999] h-full w-[380px] max-w-[100vw] bg-background-50 border-l border-background-200 shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 bg-primary-500 text-white shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                    <i className="ri-robot-2-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold whitespace-nowrap">
                      강스터디 AI
                    </h3>
                    <p className="text-xs text-white/80 whitespace-nowrap">
                      경매 전문가
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer"
                  aria-label="챗봇 닫기"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
                {/* Quick Prompts */}
                {showQuickPrompts && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="space-y-2 mb-1"
                  >
                    <p className="text-[11px] text-foreground-500 font-medium px-1">
                      이런 질문은 어떠세요?
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {QUICK_PROMPTS.map((prompt, i) => (
                        <motion.button
                          key={prompt.label}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 + i * 0.06 }}
                          onClick={() => handleQuickPrompt(prompt.message)}
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 transition-all text-left cursor-pointer group"
                        >
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all shrink-0">
                            <i className={`${prompt.icon} text-sm`}></i>
                          </div>
                          <span className="text-xs text-foreground-700 group-hover:text-foreground-950 leading-snug whitespace-normal">
                            {prompt.label}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Messages list */}
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: 0.05 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className="max-w-[85%] space-y-2">
                      {/* Case Analysis Card — shown for user messages that matched */}
                      {msg.role === 'user' && msg.caseCard && (
                        <div className="mb-2 p-3 rounded-2xl bg-background-100 border border-background-300/60 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-primary-500 text-white shrink-0">
                              <i className="ri-building-4-line text-xs"></i>
                            </div>
                            <span className="text-xs font-bold text-foreground-950 whitespace-nowrap">
                              {msg.caseCard.caseNumber} 권리분석 요약
                            </span>
                            <span
                              className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                                msg.caseCard.grade === 'A'
                                  ? 'bg-accent-100 text-accent-800'
                                  : msg.caseCard.grade === 'B'
                                    ? 'bg-secondary-100 text-secondary-800'
                                    : 'bg-foreground-100 text-foreground-700'
                              }`}
                            >
                              {msg.caseCard.grade}등급
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-background-50 rounded-lg p-2">
                              <p className="text-[10px] text-foreground-500">감정가</p>
                              <p className="text-xs font-bold text-foreground-900 whitespace-nowrap">
                                {formatKRW(msg.caseCard.appraisedValue)}
                              </p>
                            </div>
                            <div className="bg-background-50 rounded-lg p-2">
                              <p className="text-[10px] text-foreground-500">최저가</p>
                              <p className="text-xs font-bold text-primary-600 whitespace-nowrap">
                                {formatKRW(msg.caseCard.minimumPrice)}
                              </p>
                            </div>
                            <div className="bg-background-50 rounded-lg p-2">
                              <p className="text-[10px] text-foreground-500">유찰 횟수</p>
                              <p className="text-xs font-bold text-foreground-900">
                                {msg.caseCard.failureCount}회
                              </p>
                            </div>
                            <div className="bg-background-50 rounded-lg p-2">
                              <p className="text-[10px] text-foreground-500">물건유형</p>
                              <p className="text-xs font-bold text-foreground-900 whitespace-nowrap">
                                {msg.caseCard.propertyType} · {msg.caseCard.location}
                              </p>
                            </div>
                          </div>

                          <p className="text-[11px] text-foreground-600 leading-relaxed bg-background-50 rounded-lg p-2">
                            {msg.caseCard.rightsAnalysis}
                          </p>

                          <div className="flex flex-col gap-1.5">
                            <button
                              onClick={() => {
                                navigate(`/auctions/${msg.caseCard!.id}`);
                                setIsOpen(false);
                              }}
                              className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-alert-line text-sm"></i>
                              ⚠️ 특수권리 주의사항 보기
                            </button>
                            <button
                              onClick={() => {
                                navigate('/reports');
                                setIsOpen(false);
                              }}
                              className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-background-100 border border-background-300 text-foreground-700 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-file-list-3-line text-sm"></i>
                              상세 분석 리포트 구매하기
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Text bubble */}
                      <div
                        className={`px-3 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                          msg.role === 'user'
                            ? 'bg-primary-500 text-white rounded-br-md'
                            : 'bg-background-100 text-foreground-700 rounded-bl-md'
                        }`}
                      >
                        {msg.content}
                      </div>

                      {/* CTA buttons after AI response */}
                      {msg.role === 'model' && msg.ctas && idx === messages.length - 1 && (
                        <div className="flex flex-col gap-1.5 pt-1">
                          {msg.ctas.type === 'property' ? (
                            <>
                              <button
                                onClick={() => {
                                  navigate('/reports');
                                  setIsOpen(false);
                                }}
                                className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-file-search-line text-sm"></i>
                                해당 물건 리포트 보러가기
                              </button>
                              <button
                                onClick={() => {
                                  navigate('/consultation#emergency-consultation');
                                  setIsOpen(false);
                                }}
                                className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-accent-100 border border-accent-300 text-accent-800 text-xs font-bold hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-clipboard-line text-sm"></i>
                                1:1 물건상담 신청
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                navigate('/lectures');
                                setIsOpen(false);
                              }}
                              className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-secondary-500 text-white text-xs font-bold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-play-circle-line text-sm"></i>
                              관련 동영상 강의 보러가기
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {loading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="bg-background-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1.5 items-center">
                        <div
                          className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <div
                          className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <div
                          className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Community fallback — shown after 2+ user messages */}
                {userMessageCount >= 2 && !loading && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-2 p-3 rounded-2xl bg-background-100 border border-background-200/70 space-y-2"
                  >
                    <p className="text-[11px] text-foreground-500 font-medium">
                      더 자세한 답변이 필요하신가요?
                    </p>
                    <div className="flex flex-col gap-1.5">
                      <button
                        onClick={() => {
                          navigate('/community');
                          setIsOpen(false);
                        }}
                        className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-question-answer-line text-sm"></i>
                        커뮤니티 Q&A에 질문 남기기
                      </button>
                      <button
                        onClick={() => {
                          navigate('/consultation#emergency-consultation');
                          setIsOpen(false);
                        }}
                        className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-accent-100 border border-accent-300 text-accent-800 text-xs font-bold hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-phone-fill text-sm"></i>
                        전문가 긴급 전화상담
                      </button>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="px-4 py-4 border-t border-background-200 shrink-0 bg-background-50">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      if (e.target.value.length > 0 && !isTyping) setIsTyping(true);
                      if (e.target.value.length === 0 && isTyping) setIsTyping(false);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="질문을 입력하세요..."
                    className="flex-1 px-3 py-2.5 text-sm bg-background-100 rounded-xl border border-background-200 focus:outline-none focus:border-primary-400 text-foreground-950 placeholder-foreground-400"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || loading}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0 cursor-pointer"
                    aria-label="메시지 전송"
                  >
                    <i className="ri-send-plane-fill text-lg"></i>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}